import { AnimatePresence, motion } from 'framer-motion';
import type { TFunction } from 'i18next';
import numeral from 'numeral';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { ISliderProps } from '@feathr/components';
import { CardV2 as Card, Slider, Toggle, Tooltip } from '@feathr/components';
import { getIconForAction } from '@feathr/hooks';

import useLookalikeAudience, {
  SLIDER_DEFAULT_VALUE,
} from './LookalikeAudienceTargeting.useLookalikeAudience';

function getSliderMarks(t: TFunction): ISliderProps['marks'] {
  const marks = [
    { value: 0, label: t('Narrow') },
    { value: 25, label: t('Focused') },
    { value: 50, label: t('Moderate') },
    { value: 75, label: t('Broad') },
    { value: 100, label: t('Wide') },
  ];

  return marks;
}

// TODO: get the real value
// This value lets us check fake stats in a specific campaign
const MIN_REACHABLE_USERS = 360;

interface IProps {
  numReachableUsers: number;
  isLoading: boolean;
}

/** Lookalike Audience Targeting for Pixl Plus campaigns */
function LookalikeAudienceTargeting({
  numReachableUsers,
  isLoading,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  // Move this up into the parent during integration
  const { isEnabled, toggleIsEnabled, selectedAudience, setSelectedAudience } =
    useLookalikeAudience();
  const hasEnoughReachableUsers = numReachableUsers >= MIN_REACHABLE_USERS;
  const sliderMarks = getSliderMarks(t);
  const shouldShowSlider = isEnabled && hasEnoughReachableUsers;

  return (
    <Card disabled={!hasEnoughReachableUsers} width={'full'}>
      <Card.Header
        description={t(
          'Expand your reach by targeting people who are similar to your targeted groups.',
        )}
        title={t('Lookalike Audience Targeting')}
      >
        {hasEnoughReachableUsers ? (
          <Toggle onChange={toggleIsEnabled} value={isEnabled} />
        ) : (
          <Tooltip
            title={t('Lookalike targeting requires {{minReachableUsers}} reachable people.', {
              minReachableUsers: numeral(MIN_REACHABLE_USERS).format('0,0'),
            })}
          >
            {getIconForAction('locked')}
          </Tooltip>
        )}
      </Card.Header>

      <AnimatePresence>
        {shouldShowSlider && (
          <motion.div
            animate={{ opacity: 1 }}
            initial={{ opacity: 0 }}
            transition={{
              duration: 0.3,
              ease: 'easeOut',
            }}
          >
            <Card.Content isLoading={isLoading}>
              <Slider
                defaultValue={SLIDER_DEFAULT_VALUE}
                helpText={t(
                  "Use the slider below to adjust the relative size of the lookalike audience you'd like to target.",
                )}
                label={t('Lookalike Audience Size')}
                marks={sliderMarks}
                onChange={setSelectedAudience}
                preventLabelOverflow={true}
                restrictToMarks={true}
                tickLabel={null}
                value={selectedAudience}
              />
            </Card.Content>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}

export default LookalikeAudienceTargeting;
